import { Modal, Button } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import { parse } from '@wordpress/blocks';

const API_URL = '/wp-json/alpha-blocks/v1/blocks';

const AlphaLibraryModalComponent = () => {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ selectedBlockType, setSelectedBlockType ] = useState( 'All' );
	const blocksPerPage = 9;

	// Get unique block names for sidebar
	const [ blocks, setBlocks ] = useState( [] );
	const [ totalPages, setTotalPages ] = useState( 1 );
	const [ loading, setLoading ] = useState( false );
	const blockTypes = [
		'All',
		'Accordion',
		'Hero',
		'Image',
		'Heading',
		'Buttons',
	];

	// Fetch blocks from API
	useEffect( () => {
		setLoading( true );
		let url = `${ API_URL }?page=${ currentPage }&per_page=${ blocksPerPage }`;
		if ( selectedBlockType !== 'All' ) {
			url += `&type=${ selectedBlockType.toLowerCase() }`;
		}
		const headers = {};
		if ( window.wpApiSettings && window.wpApiSettings.nonce ) {
			headers[ 'X-WP-Nonce' ] = window.wpApiSettings.nonce;
		}
		fetch( url, { headers } )
			.then( ( res ) => res.json() )
			.then( ( data ) => {
				setBlocks( data.blocks || [] );
				setTotalPages( data.total_pages || 1 );
			} )
			.finally( () => setLoading( false ) );
	}, [ currentPage, selectedBlockType ] );

	useEffect( () => {
		const openHandler = () => setIsOpen( true );
		window.addEventListener( 'alphaLibraryOpen', openHandler );
		return () =>
			window.removeEventListener( 'alphaLibraryOpen', openHandler );
	}, [] );

	// Insert block into Gutenberg editor
	const insertBlock = ( block ) => {
		if ( window.wp && window.wp.data && window.wp.data.dispatch ) {
			let blockMarkup = `<!-- wp:paragraph -->
<p>This is the first block.</p>
<!-- /wp:paragraph -->

<!-- wp:image -->
<figure class="wp-block-image"><img src="example.jpg" alt="Example image"/></figure>
<!-- /wp:image -->`;

			blockMarkup = block?.content;

			console.log( 'blockMarkup: ', blockMarkup );

			const parsedBlocks = window.wp.blocks.parse( blockMarkup );

			console.log( 'parsedBlocks: ', parsedBlocks, block?.content );

			window.wp.data
				.dispatch( 'core/block-editor' )
				.insertBlocks( parsedBlocks );
			setIsOpen( false );
		} else {
			alert( 'Gutenberg editor not available' );
		}
	};

	const getStyledContent = ( content ) => {
		const parsedBlocks = window.wp.blocks.parse( content );
		const blockContent = window.wp.blocks.serialize( parsedBlocks );

		const iframeCss = `
      <style>
        body {
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
        }
      </style>
    `;
		const frontendCss =
			'<link rel="stylesheet" href="/wp-content/plugins/alpha-blocks/build/style-index.css" type="text/css" media="all">';
		return `
      <html>
        <head>
          ${ iframeCss }
          ${ frontendCss }
        </head>
        <body>
          ${ blockContent }
        </body>
      </html>
    `;
	};

    const handleDeleteBlock = (blockToDelete) => {
        if (!confirm(`Are you sure you want to delete block data with ID: ${blockToDelete.key_id}?`)) {
            return;
        }

        const headers = {};
        if (window.wpApiSettings && window.wpApiSettings.nonce) {
            headers['X-WP-Nonce'] = window.wpApiSettings.nonce;
        }

        fetch(`/wp-json/alpha-blocks/v1/delete_block_data`, {
            method: 'POST', // Using POST for now, to simplify and avoid potential CORS/preflight issues with DELETE
            headers: {
                ...headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                key_id: blockToDelete.key_id,
                name: blockToDelete.name,
                type: blockToDelete.type,
            }),
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.success) {
                // Refresh blocks after deletion
                setCurrentPage(1); // Go back to first page
                setSelectedBlockType('All'); // Reset filter
                alert('Block data deleted successfully!');
            } else {
                alert(`Error deleting block data: ${data.message}`);
            }
        })
        .catch((error) => {
            console.error('Error:', error);
            alert('Failed to delete block data.');
        });
    };

	if ( ! isOpen ) return null;

	// Paginate blocks
	// No further pagination needed, API already paginates
	const paginatedBlocks = blocks;

	return (
		<Modal
			title="Alpha Library"
			onRequestClose={ () => setIsOpen( false ) }
			shouldCloseOnClickOutside={ true }
			className="alpha-library-modal"
			style={ { maxWidth: '80vw', width: '80vw', minWidth: '320px' } }
		>
			<div style={ { display: 'flex', minHeight: '400px' } }>
				{ /* Sidebar: Block Names */ }
				<div
					style={ {
						width: '200px',
						minWidth: '180px',
						borderRight: '1px solid #eee',
						padding: '1rem',
						boxSizing: 'border-box',
					} }
				>
					<h3>Blocks</h3>
					<ul style={ { listStyle: 'none', padding: 0 } }>
						{ blockTypes.map( ( name ) => (
							<li key={ name }>
								<Button
									isPrimary={ selectedBlockType === name }
									isSecondary={ selectedBlockType !== name }
									onClick={ () => {
										setSelectedBlockType( name );
										setCurrentPage( 1 );
									} }
									style={ {
										marginBottom: '0.5rem',
										width: '100%',
									} }
								>
									{ name }
								</Button>
							</li>
						) ) }
					</ul>
				</div>
				{ /* Block Grid */ }
				<div
					style={ {
						flex: 1,
						padding: '2rem 1rem 1rem 1rem',
						position: 'relative',
					} }
				>
					<div
						style={ {
							display: 'grid',
							gridTemplateColumns: 'repeat(3, 1fr)',
							gap: '1.5rem',
							filter: loading ? 'blur(2px)' : 'none',
							pointerEvents: loading ? 'none' : 'auto',
						} }
					>
						{ paginatedBlocks?.map( ( block ) => (
							<div
								key={ block.id }
								className="alpha-block-variation-grid"
								style={ {
									border: '1px solid #ddd',
									borderRadius: '6px',
									padding: '1rem 1rem 0.7rem 1rem',
									cursor: 'pointer',
									textAlign: 'center',
									minWidth: '100px',
									minHeight: '200px',
									boxSizing: 'border-box',
									background: '#fff',
									display: 'flex',
									flexDirection: 'column', // Added to stack items
									alignItems: 'center',
									justifyContent: 'space-between',
								} }
								onClick={ () => insertBlock( block ) }
							>
								<div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								{ block.image ? (
									<img
										src={ ( window.alpbUploadImage && window.alpbUploadImage.pluginUrl )
											? ( window.alpbUploadImage.pluginUrl.replace(/\/$/, '') + '/' + String( block.image ).replace(/^\//, '') )
											: block.image }
										alt={ block.name }
										style={ {
											width: '100%',
											height: 'auto',
											objectFit: 'cover',
										} }
									/>
								) : (
									block &&
									block.content && (
										<iframe
											srcDoc={ getStyledContent(
												block.content
											) }
											style={ {
												width: '100%',
												height: 'auto',
												border: 'none',
												pointerEvents: 'none',
											} }
											title={ block.name + ' preview' }
										/>
									)
								) }
                                </div>
                                {(block.key_id || process.env.NODE_ENV === 'development') && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '5px', width: '100%', fontSize: '16px', paddingLeft: '10px', paddingRight: '10px' }}>
                                        {block.key_id && <p style={{ fontSize: '0.8em', color: 'rgb(25 25 25)', margin:'0em 0em' }}>ID: {block.key_id} ({block?.type})</p>}
                                        { process.env.NODE_ENV === 'development' && (
                                            <Button
                                                isDestructive
                                                onClick={ (e) => {
                                                    e.stopPropagation(); // Prevent block insertion
                                                    handleDeleteBlock(block);
                                                } }
                                                style={ { fontSize: '0.7em', padding: '0 5px', height:'16px' } } // Adjusted size for better fit
                                            >
                                                Delete
                                            </Button>
                                        ) }
                                    </div>
                                )}
							</div>
						) ) }
					</div>
					{ /* Loading Overlay */ }
					{ loading && (
						<div
							style={ {
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								background: 'rgba(128,128,128,0.25)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								zIndex: 2,
							} }
						>
							<div
								style={ {
									background: 'rgba(255,255,255,0.85)',
									borderRadius: '50%',
									padding: '2rem',
									boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								} }
							>
								<svg width="48" height="48" viewBox="0 0 50 50">
									<circle
										cx="25"
										cy="25"
										r="20"
										fill="none"
										stroke="#888"
										strokeWidth="5"
										strokeDasharray="31.4 31.4"
										strokeLinecap="round"
									>
										<animateTransform
											attributeName="transform"
											type="rotate"
											from="0 25 25"
											to="360 25 25"
											dur="1s"
											repeatCount="indefinite"
										/>
									</circle>
								</svg>
							</div>
						</div>
					) }
					{ /* Pagination */ }
					<div style={ { textAlign: 'center', marginTop: '2rem' } }>
						{ Array.from( { length: totalPages }, ( _, i ) => (
							<Button
								key={ i + 1 }
								isPrimary={ currentPage === i + 1 }
								isSecondary={ currentPage !== i + 1 }
								onClick={ () => setCurrentPage( i + 1 ) }
								style={ { margin: '0 0.25rem' } }
							>
								{ i + 1 }
							</Button>
						) ) }
					</div>
				</div>
			</div>
			{ /* Responsive styles */ }
			<style>{ `
        @media (max-width: 900px) {
          .alpha-library-modal {
            max-width: 98vw !important;
            width: 98vw !important;
          }
          .alpha-library-modal-content {
            padding: 1rem !important;
          }
          .alpha-library-modal-content > div {
            min-width: 0 !important;
            width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #eee !important;
          }
          .alpha-library-modal-content {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .alpha-library-modal-content {
            grid-template-columns: 1fr !important;
          }
        }

      ` }</style>
		</Modal>
	);
};

export default AlphaLibraryModalComponent;